// HR / Podpis do Gmailu — generátor jednotného e-mailového podpisu pro tým
// (19.8.2026, zadání Ládi). Owner, manager a clients manager vyberou člověka
// z HR a podpis se poskládá z jeho údajů včetně fotky. Pole jdou přepsat,
// kdyby v HR něco chybělo nebo se hodil jiný tvar pozice.
//
// E-mail v podpisu SCHVÁLNĚ NENÍ (19.8.2026, Láďa): adresát ho vidí v hlavičce
// zprávy, takže je v podpisu navíc. Pod pozicí je rovnou odkaz na web.
//
// Nic se neukládá, je to čistě generátor v prohlížeči. Žádný backend, žádná
// další tabulka, co by se mohla rozejít.
//
// PROČ TABULKOVÝ HTML A INLINE STYLY: Gmail zahazuje <style> bloky, třídy
// i většinu moderního CSS. Jediné, co spolehlivě přežije, je <table> s inline
// atributy. Proto je značkování níž takhle staromódní, není to nedbalost.
//
// FOTKA: bere se z HR (trainers.foto → /api/hr-photo nebo /api/trainer-photo),
// ale do podpisu NESMÍ jít rovnou. Gmail zahazuje object-fit, takže obdélníková
// fotka natažená do kolečka 72×72 člověka zdeformuje (19.8.2026, Láďa poslal
// vlastní roztaženou podobiznu). Ořez proto děláme tady v canvasu na čtverec
// a hotový výřez ukládáme přes /api/hr-podpis-foto. Fotka v HR zůstává beze
// změny. GET na podpisovou fotku je bez přihlášení, aby ji viděl i příjemce.
//
// LOGO: PNG na /logo-podpis.png, vyrenderované z logo.svg. SVG Gmail nevykreslí.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { PlovouciNabidka } from "../../components/ui.jsx";
import { Mail, Copy, Check, User, Briefcase, Users, Info, RefreshCw, ChevronDown, Building2 } from "lucide-react";

const BRAND = "#7C5DB0";
const DARK = "#241430";

const ZAKLAD = "https://admin.fitway40.cz";
const LOGO_URL = `${ZAKLAD}/logo-podpis.png`;
const WEB_TEXT = "www.fitway40.cz";
const WEB_URL = "https://www.fitway40.cz";

// Zkrácená doložka (přání Ládi 19.8.2026 — původní byla na půl obrazovky).
const DOLOZKA_CZ =
  "Tento e-mail je určen výlučně jeho adresátovi a může obsahovat důvěrné informace. " +
  "Pokud vám byl doručen omylem, dejte nám prosím vědět a zprávu i s přílohami smažte.";
const DOLOZKA_EN =
  "This e-mail is intended solely for its addressee and may contain confidential information. " +
  "If you received it by mistake, please let us know and delete it including any attachments.";

// Pozice se v HR místy píšou bez diakritiky. Do podpisu patří hezký tvar,
// tak nejčastější doplníme. Rod neměníme, to si člověk případně přepíše sám.
const POZICE_HEZKY = {
  trener: "Trenér",
  trenerka: "Trenérka",
  "head coach senior": "Head Coach Senior",
  "nutricni specialista": "Nutriční specialista",
  "nutricni specialistka": "Nutriční specialistka",
  "clients manager": "Clients Manager",
  sales: "Sales",
  ceo: "CEO",
};

// Obecný podpis za firmu, když se nepodepisuje konkrétní člověk (19.8.2026).
// Chová se jako další položka v seznamu, jen bez fotky.
const TYM = {
  id: "__tym__",
  display_name: "Tým FitWay40",
  pozice: "Osobní tréninky a výživové poradenství pro ženy 40+",
  foto: "",
};

const zkrasliPozici = (p) => {
  const klic = String(p || "").trim().toLowerCase();
  return POZICE_HEZKY[klic] || String(p || "").trim();
};

// Fotka z HR chodí jako relativní adresa. V e-mailu musí být absolutní.
const absolutniFotka = (foto) => {
  const f = String(foto || "").trim();
  if (!f) return "";
  if (/^https?:\/\//i.test(f)) return f;
  return ZAKLAD + (f.startsWith("/") ? f : `/${f}`);
};

// Ořez na čtverec ze středu. Gmail neumí object-fit, takže musí být hotový
// už v souboru, jinak se člověk na fotce roztáhne.
const HRANA = 288; // 2× proti 144 px, ať to je ostré i na retině

function orizniNaCtverec(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const strana = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = (img.naturalWidth - strana) / 2;
        // Portréty ořezáváme spíš od hlavy než přesně ze středu, ať se
        // neuřízne obličej u fotek na výšku.
        const sy = img.naturalHeight > img.naturalWidth
          ? Math.min((img.naturalHeight - strana) / 2, img.naturalHeight * 0.12)
          : (img.naturalHeight - strana) / 2;

        const c = document.createElement("canvas");
        c.width = HRANA;
        c.height = HRANA;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, HRANA, HRANA);
        ctx.drawImage(img, sx, sy, strana, strana, 0, 0, HRANA, HRANA);
        resolve(c.toDataURL("image/jpeg", 0.9));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Fotku se nepodařilo načíst"));
    img.src = url;
  });
}

const esc = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Sestaví HTML podpisu. Držíme se tabulek a inline stylů, viz komentář nahoře.
 */
function sestavPodpis({ jmeno, pozice, fotka }) {
  const bunkaFotky = fotka
    ? `<td style="padding:0 16px 0 0;vertical-align:middle;">
            <img src="${esc(fotka)}" width="72" height="72" alt="${esc(jmeno)}"
                 style="width:72px;height:72px;border-radius:50%;object-fit:cover;display:block;border:0;" />
          </td>`
    : "";

  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;">
  <tr>
    <td style="padding:0;">
      <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          ${bunkaFotky}
          <td style="padding:0 16px 0 0;vertical-align:middle;">
            <div style="font-size:17px;font-weight:bold;color:${DARK};line-height:1.3;">${esc(jmeno)}</div>
            <div style="font-size:13px;color:${BRAND};line-height:1.6;">${esc(pozice)}</div>
            <div style="font-size:13px;line-height:1.8;">
              <a href="${WEB_URL}" style="color:${BRAND};text-decoration:none;font-weight:bold;">${WEB_TEXT}</a>
            </div>
          </td>
          <td style="padding:0 0 0 16px;border-left:2px solid ${BRAND};vertical-align:middle;">
            <a href="${WEB_URL}" style="text-decoration:none;">
              <img src="${LOGO_URL}" width="140" alt="FitWay40" style="width:140px;display:block;border:0;" />
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:12px 0 0 0;">
      <div style="border-top:1px solid #E7E2F0;padding-top:8px;font-size:10px;line-height:1.5;color:#9A93A8;max-width:560px;">
        ${DOLOZKA_CZ}<br />${DOLOZKA_EN}
      </div>
    </td>
  </tr>
</table>`;
}

// Avatar člověka. Když fotka chybí nebo se nenačte, zůstane iniciálový kolečko
// v brandu, ať seznam nedrncá.
function Avatar({ jmeno, foto, velikost = "h-9 w-9" }) {
  const [chyba, setChyba] = useState(false);
  const inicialy = String(jmeno || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase() || "")
    .join("");

  if (foto && !chyba) {
    return (
      <img
        src={foto}
        alt=""
        onError={() => setChyba(true)}
        className={`${velikost} shrink-0 rounded-full object-cover ring-2 ring-white`}
      />
    );
  }
  return (
    <div className={`${velikost} flex shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700`}>
      {inicialy || <User className="w-4 h-4" />}
    </div>
  );
}

// Vlastní výběr člověka místo nativního <select>, aby to sedělo do designu
// aplikace a šly ukázat fotky (19.8.2026).
function VyberCloveka({ lide, hodnota, onChange, loading }) {
  const [otevreno, setOtevreno] = useState(false);
  const ref = useRef(null);
  const vybrany = lide.find((x) => String(x.id) === String(hodnota)) || null;

  // Zavření klepnutím mimo a Esc řeší PlovouciNabidka (FW-148).

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOtevreno((o) => !o)}
        className={`flex w-full items-center gap-3 rounded-xl border bg-white px-3 py-2.5 text-left transition ${
          otevreno ? "border-brand-400 ring-2 ring-brand-100" : "border-slate-200 hover:border-slate-300"
        }`}
      >
        {vybrany ? (
          <>
            {vybrany.id === TYM.id ? (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
                <Building2 className="w-4 h-4" />
              </div>
            ) : (
              <Avatar jmeno={vybrany.display_name} foto={absolutniFotka(vybrany.foto)} />
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-slate-900">{vybrany.display_name}</span>
              <span className="block truncate text-xs text-slate-500">
                {vybrany.id === TYM.id ? "Obecný podpis za firmu" : zkrasliPozici(vybrany.pozice)}
              </span>
            </span>
          </>
        ) : (
          <>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <Users className="w-4 h-4" />
            </div>
            <span className="flex-1 text-sm text-slate-400">{loading ? "Načítám tým…" : "Vyber člověka"}</span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition ${otevreno ? "rotate-180" : ""}`} />
      </button>

      <PlovouciNabidka kotva={ref} otevreno={otevreno} onZavrit={() => setOtevreno(false)} className="p-1" maxVyska={320}>
          {!lide.length && (
            <div className="px-3 py-4 text-center text-sm text-slate-400">
              {loading ? "Načítám tým…" : "Žádní aktivní lidé"}
            </div>
          )}
          {lide.map((c) => {
            const jeVybrany = String(c.id) === String(hodnota);
            const jeTym = c.id === TYM.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { onChange(String(c.id)); setOtevreno(false); }}
                className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition ${
                  jeVybrany ? "bg-brand-50" : "hover:bg-slate-50"
                } ${jeTym ? "mb-1 border-b border-slate-100 pb-2.5" : ""}`}
              >
                {jeTym ? (
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
                    <Building2 className="w-4 h-4" />
                  </div>
                ) : (
                  <Avatar jmeno={c.display_name} foto={absolutniFotka(c.foto)} />
                )}
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-sm ${jeVybrany ? "font-bold text-brand-800" : "font-medium text-slate-800"}`}>
                    {c.display_name}
                  </span>
                  <span className="block truncate text-xs text-slate-500">
                    {jeTym ? "Obecný podpis za firmu" : zkrasliPozici(c.pozice)}
                  </span>
                </span>
                {jeVybrany && <Check className="w-4 h-4 shrink-0 text-brand-600" />}
              </button>
            );
          })}
        </PlovouciNabidka>
    </div>
  );
}

function Pole({ label, ikona: Ikona, ...props }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
        {Ikona && <Ikona className="w-4 h-4 text-slate-400" />} {label}
      </span>
      <input
        {...props}
        className="field mt-1 w-full"
      />
    </label>
  );
}

export default function PodpisGmail() {
  const { data, loading } = useApiData("/api/hr-list-people");
  const [kdo, setKdo] = useState("");
  const [form, setForm] = useState({ jmeno: "", pozice: "", fotka: "" });
  const [zkopirovano, setZkopirovano] = useState(false);
  // nic | pripravuji | hotovo | chybi | chyba
  const [fotoStav, setFotoStav] = useState("nic");
  const nahledRef = useRef(null);

  // Ukončení a bývalí do podpisu nepatří (19.8.2026). Pole `active` je u všech
  // true, rozlišuje až `status` — "Aktivní" vs. "Ukončeno" a "bývalý".
  const lide = useMemo(() => {
    const p = Array.isArray(data?.people) ? data.people : [];
    const jeAktivni = (x) => {
      if (x.active === false) return false;
      const st = String(x.status || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return !st || st.startsWith("aktiv");
    };
    const aktivni = p
      .filter(jeAktivni)
      .sort((a, b) => String(a.display_name || "").localeCompare(String(b.display_name || ""), "cs"));
    return [TYM, ...aktivni];
  }, [data]);

  // Po výběru člověka se pole naplní z HR. Zůstávají editovatelná, kdyby
  // v HR chyběla pozice nebo se hodil jiný tvar.
  //
  // Fotka se u toho ořízne na čtverec a uloží jako podpisová varianta, jinak
  // by ji Gmail v kolečku roztáhl. Když se to nepovede, podpis se vykreslí
  // bez fotky, ať radši chybí, než aby na ní člověk vypadal zdeformovaně.
  useEffect(() => {
    if (!kdo) { setFotoStav("nic"); return; }
    const c = lide.find((x) => String(x.id) === String(kdo));
    if (!c) return;

    setForm({
      jmeno: String(c.display_name || "").trim(),
      pozice: zkrasliPozici(c.pozice),
      fotka: "",
    });

    // Firemní podpis se nepodepisuje fotkou, nese ho logo vpravo.
    if (c.id === TYM.id) { setFotoStav("tym"); return; }

    const zdroj = absolutniFotka(c.foto);
    if (!zdroj) { setFotoStav("chybi"); return; }

    let zruseno = false;
    setFotoStav("pripravuji");
    (async () => {
      try {
        const ctverec = await orizniNaCtverec(zdroj);
        const r = await apiFetch("/api/hr-podpis-foto", {
          method: "POST",
          body: JSON.stringify({ personId: c.id, dataUrl: ctverec }),
        });
        if (!r.ok) throw new Error("Uložení podpisové fotky selhalo");
        if (zruseno) return;
        setForm((f) => ({ ...f, fotka: `${ZAKLAD}/api/hr-podpis-foto?id=${encodeURIComponent(c.id)}&v=${Date.now()}` }));
        setFotoStav("hotovo");
      } catch {
        if (!zruseno) setFotoStav("chyba");
      }
    })();
    return () => { zruseno = true; };
  }, [kdo, lide]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const vyplneno = form.jmeno.trim() && form.pozice.trim() && fotoStav !== "pripravuji";

  const html = useMemo(
    () =>
      sestavPodpis({
        jmeno: form.jmeno.trim() || "Jméno Příjmení",
        pozice: form.pozice.trim() || "Pozice",
        fotka: form.fotka.trim(),
      }),
    [form]
  );

  // Do Gmailu se musí dostat FORMÁTOVANÝ text, ne značkování. Proto se do
  // schránky dává rovnou text/html. Starší prohlížeče ClipboardItem neumí,
  // tam se náhled označí a zkopíruje postaru.
  const zkopirovat = async () => {
    try {
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([nahledRef.current?.innerText || ""], { type: "text/plain" }),
          }),
        ]);
      } else {
        const rozsah = document.createRange();
        rozsah.selectNodeContents(nahledRef.current);
        const vyber = window.getSelection();
        vyber.removeAllRanges();
        vyber.addRange(rozsah);
        document.execCommand("copy");
        vyber.removeAllRanges();
      }
      setZkopirovano(true);
      setTimeout(() => setZkopirovano(false), 2200);
    } catch {
      /* schránka nedostupná, zbývá ruční označení náhledu */
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-5xl">
      <GradientHeader
        variant="purple"
        icon={Mail}
        eyebrow="HR"
        title="Podpis do Gmailu"
        subtitle="Vyber člověka z týmu a zkopíruj hotový podpis"
        loading={loading}
      />

      <div className="grid gap-5 lg:grid-cols-2">
        {/* ------------------------------------------------------- formulář */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <Users className="w-4 h-4 text-slate-400" /> Člověk z týmu
          </div>
          <VyberCloveka lide={lide} hodnota={kdo} onChange={setKdo} loading={loading} />

          {kdo && (
            <div className="mt-5 space-y-4 border-t border-slate-100 pt-5">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-bold text-slate-900">
                  {kdo === TYM.id ? "Údaje podpisu" : "Údaje z HR"}
                </h2>
                <span className="text-xs text-slate-400">jdou přepsat</span>
              </div>
              <Pole
                label={kdo === TYM.id ? "Název" : "Celé jméno"}
                ikona={User}
                value={form.jmeno}
                onChange={set("jmeno")}
              />
              <Pole
                label={kdo === TYM.id ? "Popisek pod názvem" : "Pozice"}
                ikona={Briefcase}
                value={form.pozice}
                onChange={set("pozice")}
              />

              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                {form.fotka ? (
                  <img src={form.fotka} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-white" />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-400">
                    {fotoStav === "pripravuji" ? <RefreshCw className="w-5 h-5 animate-spin" /> : <User className="w-5 h-5" />}
                  </div>
                )}
                <div className="min-w-0 text-xs leading-relaxed text-slate-500">
                  {fotoStav === "pripravuji" && "Ořezávám fotku na čtverec, ať se v podpisu neroztáhne…"}
                  {fotoStav === "hotovo" && "Fotka je oříznutá na čtverec, aby se v kolečku nezdeformovala. Originál v HR zůstal beze změny."}
                  {fotoStav === "chybi" && "Tenhle člověk nemá v HR fotku, podpis bude bez ní. Nahraješ ji v HR → Lidé."}
                  {fotoStav === "chyba" && "Fotku se nepodařilo připravit, podpis bude bez ní. Zkus vybrat člověka znovu."}
                  {fotoStav === "tym" && "Firemní podpis je bez fotky, nese ho logo vpravo. Popisek pod názvem si můžeš upravit."}
                </div>
              </div>
            </div>
          )}

          {!kdo && (
            <p className="mt-4 text-sm text-slate-400">
              Vyber člověka a podpis se poskládá sám z jeho údajů v HR.
            </p>
          )}
        </div>

        {/* --------------------------------------------------------- náhled */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-bold text-slate-900">Náhled</h2>
            <button
              type="button"
              onClick={zkopirovat}
              disabled={!vyplneno}
              className={`btn ${
                zkopirovano
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : vyplneno
                  ? "btn-primary"
                  : "cursor-not-allowed bg-slate-100 text-slate-400"
              }`}
            >
              {zkopirovano ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {zkopirovano ? "Zkopírováno" : "Zkopírovat podpis"}
            </button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div ref={nahledRef} dangerouslySetInnerHTML={{ __html: html }} />
          </div>

          {!vyplneno && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
              <RefreshCw className="w-3.5 h-3.5" /> Náhled zatím ukazuje zástupné údaje.
            </p>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------ návod */}
      <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-brand-900">
          <Info className="w-4 h-4" /> Jak ho nasadit v Gmailu
        </h3>
        <ol className="space-y-1.5 text-sm text-brand-900">
          <li>1. Zkopírujte podpis tlačítkem nahoře.</li>
          <li>2. V Gmailu otevřete Nastavení, ozubené kolo vpravo nahoře, a dejte Zobrazit všechna nastavení.</li>
          <li>3. Na kartě Obecné sjeďte dolů na Podpis a vytvořte nový.</li>
          <li>4. Do pole podpisu vložte zkopírovaný obsah zkratkou Cmd+V, na Windows Ctrl+V.</li>
          <li>5. Dole nastavte, aby se podpis používal u nových zpráv i u odpovědí, a uložte změny.</li>
        </ol>
        <p className="mt-3 text-xs leading-relaxed text-brand-800">
          Logo i fotka se do e-mailu vkládají odkazem, ne přílohou, takže podpis nezvětšuje zprávu.
          Některým příjemcům se obrázky nenačtou, dokud je nepovolí, proto podpis funguje i bez nich.
        </p>
      </div>
    </div>
  );
}
